import { useSelectionManager } from "@prismicio/editor-fields"
import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import { z } from "zod"

import { useDocumentListData } from "../DocumentListBody/useDocumentListData"
import { useBulkAction } from "./BulkActionsProvider"

interface UseBulkActionsButtonProps {
  hasPermission?: boolean
  releaseId?: string
}

export function useBulkActionsButton(props: UseBulkActionsButtonProps = {}) {
  const { hasPermission = true, releaseId } = props
  const { action, documentsInQueue } = useBulkAction()
  const { selectedCount, isSelected } = useSelectionManager()
  const tab = useTabFromRoute()

  const {
    data: { documents },
  } = useDocumentListData({ tab, releaseId })

  function getSelectedDocuments() {
    return documents.filter(
      (document) =>
        isSelected(document.id) &&
        !documentsInQueue.find((queuedDocument) =>
          "documentId" in queuedDocument
            ? queuedDocument.documentId === document.id
            : queuedDocument.documentGroupLangId === document.group_lang_id,
        ),
    )
  }

  let disabled = false
  let tooltip: string | undefined = undefined

  if (!hasPermission) {
    disabled = true
    tooltip = "Your current role can't perform this action"
  } else if (selectedCount === 0) {
    disabled = true
    tooltip = "No documents selected"
  } else if (selectedCount === documentsInQueue.length) {
    disabled = true
    tooltip = "All documents are already being processed"
  }

  return {
    action,
    disabled,
    tooltip,
    getSelectedDocuments,
  }
}

const tabSchema = z.enum(["working", "archived", "upcoming"])

function useTabFromRoute(): z.infer<typeof tabSchema> {
  const location = useLocation()

  const tab = useMemo(() => {
    const pathSegments = location.pathname
      .split("/")
      .filter((segment) => segment !== "")
    const extractedTab = pathSegments[0]

    // NOTE: Sometimes this runs before the redirect to /working
    // happens, so we need to handle this case
    if (!extractedTab) return "working"

    // NOTE: /migration route actually behaves just like /upcoming,
    // but focus only on the Migration Release, so, we need to parse this here
    if (extractedTab === "migration") return "upcoming"

    return tabSchema.parse(extractedTab)
  }, [location.pathname])

  return tab
}
